import { createApp } from 'vue'
import i18n from './i18n'
import { applyStoredLocale } from './i18n/locale'
import { createVuestic } from 'vuestic-ui'
import { createGtm } from '@gtm-support/vue-gtm'

import stores from './stores'
import { useAuthStore } from '@/stores/auth.store'
import router from './router'
import vuesticGlobalConfig from './services/vuestic-ui/global-config'
import App from './App.vue'

import './registerServiceWorker'

const app = createApp(App)

app.use(stores)
app.use(router)
app.use(i18n)

// The language chosen in the profile menu, applied before anything renders.
//
// Here and not in i18n/index.ts: the choice lives in the filter store (the
// project's pattern for a persistent user setting), and a Pinia store can only
// be used once app.use(stores) has run. i18n/index.ts is evaluated at import
// time — earlier than that — so reading the store there would fail with "no
// active Pinia". Placed before app.mount() below, so the first paint is
// already in the right language instead of flashing German.
applyStoredLocale()

app.use(createVuestic({ config: vuesticGlobalConfig }))

import ganttastic from '@infectoone/vue-ganttastic'
app.use(ganttastic)

import { library, dom } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { fas } from '@fortawesome/free-solid-svg-icons'
import { fab } from '@fortawesome/free-brands-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
library.add(fas, far, fab)
dom.watch();
app.component("font-awesome-icon", FontAwesomeIcon)

/*
if (import.meta.env.VITE_APP_GTM_ENABLED) {
  app.use(
    createGtm({
      id: import.meta.env.VITE_APP_GTM_KEY,
      debug: false,
      vueRouter: router,
    }),
  )
}
*/

app.config.globalProperties.$methods = {
  resolve(path: string, obj=self, separator='.') {
    let properties = Array.isArray(path) ? path : path.split(separator)
    return properties.reduce((prev, curr) => prev?.[curr], obj)
  }
}

// Global number formatter — used in all amount/price/total/vat displays.
// Returns a string with exactly two decimals. `null`/`undefined`/non-numeric
// values render as "0,00" so the UI never shows "NaN" or "undefined".
//
// A value typed into a form is still a string, and the forms are German: a
// price entered as "9,50" reached Number() as NaN and the row showed 0,00 €
// until the record had been saved once and came back as a real number. The
// same parsing as the backend setter (api/app/models/helpers/decimalInput.js),
// so what is displayed matches what gets stored: a dot only counts as a
// thousands separator when a comma follows it.
const toNumber = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return Number(value);

  const trimmed = value.trim();
  if (trimmed === '') return NaN;

  const normalised = trimmed.includes(',')
    ? trimmed.replace(/\./g, '').replace(',', '.')
    : trimmed;

  return Number(normalised);
}

app.config.globalProperties.$fmt = {
  num (value: any, decimals: number = 2): string {
    const n = toNumber(value);
    if (!isFinite(n)) return (0).toFixed(decimals).replace('.', ',');
    return n.toFixed(decimals).replace('.', ',');
  },

  // Line total. Multiplying in the template multiplied the raw values, so a
  // price still held as the typed string turned the product into NaN and the
  // cell showed 0,00 € next to a perfectly good price. Both sides are parsed
  // the same way num() parses.
  mul (a: any, b: any, decimals: number = 2): string {
    return this.num(toNumber(a) * toNumber(b), decimals);
  },

  // The same parsing as a number, for the document totals. A total that sums
  // the raw values collapses to NaN as soon as one position carries a typed
  // string or no price at all — and the guard around it then showed 0,00 for
  // the whole document. Anything unparseable counts as zero, so a missing
  // price costs its own line and not the sum.
  n (value: any): number {
    const parsed = toNumber(value);
    return isFinite(parsed) ? parsed : 0;
  }
}

// Permission helpers. Resolved against the current user's role.permissions[].
// Use in templates: `v-if="$can.editArticle()"` to hide write actions.
app.config.globalProperties.$can = {
  hasPermission (name: string): boolean {
    const authStore = useAuthStore();
    const perms = authStore?.user?.role?.permissions || [];
    return perms.some((p: any) => p?.name === name);
  },
  isReadOnlyArticle (): boolean {
    // Admin always overrides any restriction.
    if (this.hasPermission('admin')) return false;
    return this.hasPermission('only_article_read');
  },
  editArticle (): boolean {
    return !this.isReadOnlyArticle();
  }
}

app.config.globalProperties.$copyClipboard = {
  do: ( value: string ) => {
    navigator.clipboard.writeText(value);

    app.config.globalProperties.$vaToast.init({ message: 'Copied: ' + value, color: 'success' });
  }
}

app.config.globalProperties.$validationRules = {
  requiredRule: ( error_message:string ) => {
    const rule = (value:any) => {
      if(value === undefined || value === null) {
        return error_message
      } else {
        return ('' + value).length > 0 || error_message
      }
    }
    return rule;
  },

  dateRule: ( error_message:string ) => {
    const rule = (value:any) => {
      if (!value) {
        return true;
      }
      if(!(value instanceof Date)) {
        return true;
      }

      let year = value.getFullYear();
      let actual_year = new Date().getFullYear()

      return ( year >= actual_year - 120 && year <= actual_year + 120) || error_message;
    }
    return rule;
  },

  requiredRuleVisibleNOT: ( error_message:string, item:any, key:string ) => {
    const rule = (value:any) => {
      if(!item[key]) {
        return true
      }

      if(value === undefined || value === null) {
        return error_message
      } else {
        return ('' + value).length > 0 || error_message
      }
    }
    return rule;
  },

  requiredRuleValuesIn: ( error_message:string, item:any, key:string, values:Array<string> ) => {
    const rule = (value:any) => {
      let _check = null;

      try {
        _check = key.split(/"|'|\]|\.|\[/).reduce((s,c)=>c===""?s:s&&s[c], item)
      } catch (error) {
        return true;
      }

      if(values.includes(_check)) {
        if(value === undefined || value === null) {
          return error_message
        } else {
          return ('' + value).length > 0 || error_message
        }
      } else {
        return true
      }
    }
    return rule;
  },

  emailRule: ( error_message:string ) => {
    const rule = (value:any) => {
      if (!value || value === '') return true;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value) || error_message;
    }
    return rule;
  },

  phoneRule: ( error_message:string ) => {
    const rule = (value:any) => {
      if (!value || value === '') return true;
      const phoneRegex = /^[+\d][\d\s\-()./]{4,}$/;
      return phoneRegex.test(value) || error_message;
    }
    return rule;
  },

  ibanRule: ( error_message:string ) => {
    const rule = (value:any) => {
      if (!value || value === '') return true;
      const ibanClean = value.replace(/\s/g, '').toUpperCase();
      const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4,}$/;
      return ibanRegex.test(ibanClean) || error_message;
    }
    return rule;
  },

  urlRule: ( error_message:string ) => {
    const rule = (value:any) => {
      if (!value || value === '') return true;
      try {
        const url = value.startsWith('http') ? value : 'https://' + value;
        new URL(url);
        return true;
      } catch {
        return error_message;
      }
    }
    return rule;
  },

  minLengthRule: ( error_message:string, min:number ) => {
    const rule = (value:any) => {
      if (!value || value === '') return true;
      return ('' + value).length >= min || error_message;
    }
    return rule;
  }
}

app.mount('#app')
